import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import {
  Appointment,
  Referral,
  Treatment,
  TreatmentPanelFilter,
} from '../../types/treatments.types'
import { TreatmentStatus } from '../../../../../models/graphqlTypes'
import { usePermissions } from '../../../../../common/hooks/usePermissions'
import {
  HoveredTreatmentItem,
  TreatmentPanelTab,
} from '../../types/treatmentPanel.types'

const emptyTreatment = {
  __typename: 'Treatment',
  id: '',
  createdAt: null,
  updatedAt: null,
  doctor: {
    __typename: 'DoctorProfile',
    id: '',
    title: '',
    firstName: '',
    lastName: '',
    stampNumber: '',
    userId: '',
    professions: [],
    institutionToDoctor: [],
    assistants: [],
  },
  appointments: [],
  patient: {
    __typename: 'PatientProfile',
    id: '',
    firstName: '',
    lastName: '',
    title: '',
    tajNumber: '',
    phone: '',
  },
  surveys: [],
  referrals: [],
  title: {
    __typename: 'MultiLangText',
    hu: '',
    en: '',
  },
  description: {
    __typename: 'MultiLangText',
    hu: '',
    en: '',
  },
  bnoCodes: [],
} as Treatment

interface TreatmentPanelContextProps {
  treatment: Treatment
  refetchTreatment: () => void
}

interface TreatmentPanelContextValues extends TreatmentPanelContextProps {
  isTreatmentDraft: boolean
  isTreatmentDeleted: boolean
  isOwnTreatment: boolean
  isAssistantOfTreatment: boolean
  activeView: TreatmentPanelTab
  toggleActiveView: () => void
  showArrows: boolean
  toggleShowArrows: () => void
  showPastTreatmentItems: boolean
  setShowPastTreatmentItems: (showPast: boolean) => void
  hoveredTreatmentItem: HoveredTreatmentItem | null
  setHoveredTreatmentItem: (id: HoveredTreatmentItem | null) => void
  filterValue: TreatmentPanelFilter
  setFilterValue: React.Dispatch<React.SetStateAction<TreatmentPanelFilter>>
  clearFilterValue: () => void
  linkingDisabledIds: string[]
  setLinkingDisabledIds: (ids: string[]) => void
  linkingAppointments: Appointment[]
  setLinkingAppointments: React.Dispatch<React.SetStateAction<Appointment[]>>
  isAddReferralModalOpen: boolean
  onOpenReferralModal: () => void
  onCloseReferralModal: () => void
  referralToShow: Referral | undefined
  onViewReferral: (referraL: Referral) => void
}

const defaultValues: TreatmentPanelContextValues = {
  treatment: emptyTreatment,
  refetchTreatment: () => {},
  isTreatmentDraft: true,
  isTreatmentDeleted: false,
  isOwnTreatment: false,
  isAssistantOfTreatment: false,
  activeView: TreatmentPanelTab.Events,
  toggleActiveView: () => {},
  showArrows: false,
  toggleShowArrows: () => {},
  showPastTreatmentItems: false,
  setShowPastTreatmentItems: () => {},
  hoveredTreatmentItem: null,
  setHoveredTreatmentItem: () => {},
  filterValue: {},
  setFilterValue: () => {},
  clearFilterValue: () => {},
  linkingDisabledIds: [],
  setLinkingDisabledIds: () => {},
  linkingAppointments: [],
  setLinkingAppointments: () => {},
  isAddReferralModalOpen: false,
  onOpenReferralModal: () => {},
  onCloseReferralModal: () => {},
  referralToShow: undefined,
  onViewReferral: () => {},
}

export const TreatmentPanelContext =
  createContext<TreatmentPanelContextValues>(defaultValues)

export const useTreatmentPanelData = (): TreatmentPanelContextValues =>
  useContext(TreatmentPanelContext)

export const TreatmentPanelProvider: React.FC<
  React.PropsWithChildren<TreatmentPanelContextProps>
> = ({ treatment, refetchTreatment, children }) => {
  // Show treatment items or logs
  const [activeView, setActiveView] = useState<TreatmentPanelTab>(
    TreatmentPanelTab.Events
  )
  const toggleActiveView = useCallback(
    () =>
      setActiveView(
        activeView === TreatmentPanelTab.Events
          ? TreatmentPanelTab.TreatmentLog
          : TreatmentPanelTab.Events
      ),
    [activeView]
  )

  // Visible features
  const [showArrows, setShowArrows] = useState<boolean>(false)
  const toggleShowArrows = useCallback(
    () => setShowArrows(!showArrows),
    [showArrows]
  )

  const [showPastTreatmentItems, setShowPastTreatmentItems] =
    useState<boolean>(false)

  const [hoveredTreatmentItem, setHoveredTreatmentItem] =
    useState<HoveredTreatmentItem | null>(null)

  // Filter
  const emptyFilter = useMemo(
    () => ({
      searchTerm: '',
      isAppointment: true,
      isSurvey: true,
      isReferral: true,
    }),
    []
  )
  const [filterValue, setFilterValue] =
    useState<TreatmentPanelFilter>(emptyFilter)

  const clearFilterValue = useCallback(() => {
    setFilterValue(emptyFilter)
  }, [emptyFilter])

  // Linking
  const [linkingDisabledIds, setLinkingDisabledIds] = useState<string[]>([])
  const [linkingAppointments, setLinkingAppointments] = useState<Appointment[]>(
    []
  )

  // Modals

  // Referral
  const [isAddReferralModalOpen, setAddReferralModalOpen] =
    useState<boolean>(false)
  const [referralToShow, setReferralToShow] = useState<Referral | undefined>(
    undefined
  )

  // Treatment properties
  const { isOwnTreatment, isAssistantOfTreatment } = usePermissions(
    treatment?.doctor.id
  )

  const isTreatmentDraft = treatment.status === TreatmentStatus.Draft
  const isTreatmentDeleted = treatment?.status === TreatmentStatus.Cancelled

  const onOpenReferralModal = () => setAddReferralModalOpen(true)
  const onCloseReferralModal = () => {
    setAddReferralModalOpen(false)
    setReferralToShow(undefined)
  }
  const onViewReferral = useCallback((referral: Referral) => {
    onOpenReferralModal()
    setReferralToShow(referral)
  }, [])

  const value = useMemo(
    () => ({
      treatment,
      refetchTreatment,
      isTreatmentDraft,
      isTreatmentDeleted,
      isOwnTreatment,
      isAssistantOfTreatment,
      activeView,
      toggleActiveView,
      showArrows,
      toggleShowArrows,
      showPastTreatmentItems,
      setShowPastTreatmentItems,
      hoveredTreatmentItem,
      setHoveredTreatmentItem,
      filterValue,
      setFilterValue,
      clearFilterValue,
      linkingDisabledIds,
      setLinkingDisabledIds,
      linkingAppointments,
      setLinkingAppointments,
      isAddReferralModalOpen,
      onOpenReferralModal,
      onCloseReferralModal,
      referralToShow,
      onViewReferral,
    }),
    [
      activeView,
      clearFilterValue,
      filterValue,
      hoveredTreatmentItem,
      isAddReferralModalOpen,
      isAssistantOfTreatment,
      isOwnTreatment,
      isTreatmentDeleted,
      isTreatmentDraft,
      linkingAppointments,
      linkingDisabledIds,
      onViewReferral,
      referralToShow,
      refetchTreatment,
      showArrows,
      showPastTreatmentItems,
      toggleActiveView,
      toggleShowArrows,
      treatment,
    ]
  )
  return (
    <TreatmentPanelContext.Provider value={value}>
      {children}
    </TreatmentPanelContext.Provider>
  )
}
